<template>
    <v-container v-if="survey">
        <v-form ref="form" lazy-validation>
            <div class="blockquote">Wählen Sie die Neophyten-Typ aus, für den Sie die Erhebung erfassen.</div>
            <v-row>
                <v-col cols="12" md="6" lg="5">
                    <v-select v-model="survey.neophyte" :label="$t('key.neophyte')" prepend-icon="mdi-sprout"
                              :items="neophytes" :rules="neuphyteRules" @change="onPlantSelectionChange"></v-select>
                </v-col>
                <v-col md="6" lg="5" offset-lg="2">
                    <v-select v-model="survey.vegetationStage" :label="$t('key.stadium')" prepend-icon="mdi-stairs"
                              :items="vegetationStages" :rules="vegetationStageRules"></v-select>
                    </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <PlantPreview :plantId="plantIdSelection"></PlantPreview>
                </v-col>
            </v-row>

            <div class="blockquote">Wählen Sie den Ort des Neophyten auf der Karte aus. Markieren Sie dessen Position oder zeichnen Sie die Fläche ein. <span v-if="currentPosition">Ihre GPS Koordinaten lauten: {{currentPosition.lat}} / {{currentPosition.lng}}</span></div>

            <MapEditor :shape="survey.shape" :plantId="plantIdSelection" @shape-change="onShapeChange"></MapEditor>

            <v-row>
                <v-col cols="12" md="6" lg="5">
                    <v-text-field v-model="survey.propertyNumber" :label="$t('key.propertyNumber')" prepend-icon="mdi-map"></v-text-field>
                </v-col>
            </v-row>

            <v-row v-if="mapCircleEnabled() || mapPolygonEnabled()">
                <v-col cols="12" md="3" lg="2">
                    <v-text-field v-model="survey.areaOfPlants" type="number" :label="$t('key.areaOfPlants')" prepend-icon="mdi-texture-box" :suffix="$t('key.areaOfPlantsUnit')"></v-text-field>
                </v-col>
                <v-col md="3" offset-md="3" lg="2" offset-lg="5">
                    <v-text-field v-model="survey.numberOfPlants" type="number" :label="$t('key.numberOfPlants')" prepend-icon="mdi-sigma" :suffix="$t('key.numberOfPlantsUnit')"></v-text-field>
                </v-col>
            </v-row>

            <div class="blockquote">Fügen Sie Details zur Erhebung an, die für die Abrechnung benötigt werden.</div>

            <v-row>
                <v-col>
                    <v-text-field v-model="survey.customer" :label="$t('key.customer')" :rules="customerRules" prepend-icon="mdi-account"></v-text-field>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="6" lg="5">
                    <v-select v-model="survey.actionType" :label="$t('key.actionType')" :rules="actionTypeRules" :items="actionTypes" prepend-icon="mdi-clipboard-edit" @change="onActionSelectionChange"></v-select>
                </v-col>

                <v-col md="6" lg="5" offset-lg="2" v-if="mapActionsEnabled()">
                    <v-select v-model="survey.action" :label="$t('key.action')" :rules="actionRules" :items="actions" prepend-icon="mdi-shovel"></v-select>
                </v-col>
            </v-row>

            <v-row v-if="mapAmountEnabled()">
                <v-col cols="12" md="3" lg="2">
                    <v-text-field v-model="survey.amountValue" :label="$t('key.amountValue')" prepend-icon="mdi-numeric"></v-text-field>
                </v-col>
                <v-col md="6" offset-md="3" lg="5" offset-lg="5">
                    <v-select v-model="survey.amountUnit" :label="$t('key.amountUnit')" prepend-icon="mdi-123" :items="quantities"></v-select>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="6" lg="5">
                    <v-menu v-model="dateMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                        <template v-slot:activator="{ on }">
                            <v-text-field v-model="survey.date" :label="$t('key.date')" prepend-icon="mdi-calendar-range" readonly v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="survey.date" @input="dateMenu = false"></v-date-picker>
                    </v-menu>
                </v-col>
                <v-col md="6" lg="5" offset-lg="2">
                    <v-text-field v-model="survey.contractor" :label="$t('key.contractor')" prepend-icon="mdi-account-tie" disabled></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3" lg="2">
                    <v-menu ref="startMenu" v-model="timeStartMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                        <template v-slot:activator="{ on }">
                            <v-text-field v-model="survey.timeStart" :label="$t('key.timeStart')" prepend-icon="mdi-clock-start" readonly v-on="on"></v-text-field>
                        </template>
                        <v-time-picker v-model="survey.timeStart" @change="onTimeChange" v-if="timeStartMenu"></v-time-picker>
                    </v-menu>
                </v-col>
                <v-col md="3" offset-md="3" lg="2" offset-lg="5">
                    <v-menu ref="endMenu" v-model="timeEndMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                        <template v-slot:activator="{ on }">
                            <v-text-field v-model="survey.timeEnd" :label="$t('key.timeEnd')" prepend-icon="mdi-clock-end" readonly v-on="on"></v-text-field>
                        </template>
                        <v-time-picker v-model="survey.timeEnd" :min="survey.timeStart" @change="onTimeChange" v-if="timeEndMenu"></v-time-picker>
                    </v-menu>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-textarea v-model="survey.note" :label="$t('key.note')" prepend-icon="mdi-comment"></v-textarea>
                </v-col>
            </v-row>

            <div class="blockquote">Laden Sie Bilder der Erhebung hoch. Maximal 5 Bilder sind zulässig.</div>

            <div class="images-upload">
                <ImageEditor ref="imageeditor" imageType="survey" :refId="survey.uuid"  maxFiles="5"></ImageEditor>
            </div>

            <v-row>
                <v-spacer></v-spacer>
                <v-btn dark class="green lighten-1 ma-2" min-width="100px" @click="onSubmit">{{$t('app.save')}}</v-btn>
                <v-btn class="ma-2" min-width="100px" @click="onCancel">{{$t('app.cancel')}}</v-btn>
            </v-row>
        </v-form>
    </v-container>
</template>

<script>
    import moment from 'moment'

    import {computeArea} from 'spherical-geometry-js'
    import {uuid} from 'vue-uuid';

    import {ActionTypes, Actions, Quantities, Shapes, VegetationStages, Roles} from '../../_helpers'
    import MapEditor from '../map/MapEditor'
    import ImageEditor from '../../base/ImageEditorDirect'
    import PlantPreview from '../plant/PlantPreview';

    export default {
        name: 'SurveyForm',
        components: {
            PlantPreview,
            MapEditor,
            ImageEditor
        },
        props: ['surveyId', 'parentId'],
        computed: {
            userProfile() {
                return this.$store.getters.userProfile
            },
            neophytes() {
                let items = []
                this.$store.state.plant.plantList.forEach(item =>
                    items.push({text: item.name, value: item.uuid})
                )
                return items
            },
            vegetationStages() {
                return VegetationStages.list()
            },
            actionTypes() {
                if(!this.parentId)
                    return ActionTypes.list()
                else
                    return ActionTypes.list().slice(1)
            },
            actions() {
                return Actions.list()
            },
            quantities() {
                return Quantities.list()
            },
            survey() {
                if (this.surveyId) {
                    // find given prop in list of surveys
                    let survey = this.$store.getters.surveyList.find(item => item.uuid === this.surveyId)
                    // manual initialize plantIdSelection to trigger map functions
                    this.onPlantSelectionChange(survey.neophyte)
                    return survey
                }
                if (this.parentId) {
                    // find parent survey and get some data form it.
                    let parentSurvey = this.$store.getters.surveyList.find(item => item.uuid === this.parentId)
                    // manual initialize plantIdSelection to trigger map functions
                    this.onPlantSelectionChange(parentSurvey.neophyte)
                    return {
                        uuid: this.temporaryId,
                        parentid: this.parentId,
                        neophyte: parentSurvey.neophyte,
                        shape:parentSurvey.shape,
                        propertyNumber: null,
                        vegetationStage: null,
                        numberOfPlants: null,
                        areaOfPlants: null,
                        position: null,
                        actionType: null,
                        action: null,
                        amountValue: null,
                        amountUnit: null,
                        date: moment().format("YYYY-MM-DD"),
                        timeStart: moment().subtract(10, 'm').format("kk:mm"),
                        timeEnd: moment().format("kk:mm"),
                        customer: parentSurvey.customer,
                        note: null,
                        comment: null,
                        assignedWorker: null,
                        contractor: parentSurvey.contractor
                    }
                }

                // default action
                return {
                    uuid: this.temporaryId,
                    neophyte: null,
                    // shape
                    propertyNumber: null,
                    vegetationStage: null,
                    numberOfPlants: null,
                    areaOfPlants: null,
                    position: null,
                    actionType: null,
                    action: null,
                    amountValue: null,
                    amountUnit: null,
                    date: moment().format("YYYY-MM-DD"),
                    timeStart: moment().subtract(10, 'm').format("kk:mm"),
                    timeEnd: moment().format("kk:mm"),
                    customer: null,
                    note: null,
                    comment: null,
                    contractor: this.userProfile.email
                }
            }
        },
        data: () => ({
            currentPosition: null,
            temporaryId : null,
            // Reactive properties
            plantIdSelection: null,
            actionSelection: null,

            dateMenu: false,
            date: new Date().toISOString().substr(0, 10),
            timeStartMenu: false,
            timeStart: null,
            timeEndMenu: false,
            timeEnd: null,
            location: null,
            imageIds: [],

            neuphyteRules: [
                v => !!v
            ],
            vegetationStageRules: [
                v => !!v
            ],
            customerRules: [
                v => !!v
            ],
            actionTypeRules: [
                v => !!v
            ],
            actionRules: [
                v => !!v
            ]
        }),
        methods: {
            onPlantSelectionChange(value) {
                this.plantIdSelection = value
            },
            onActionSelectionChange(value) {
                this.actionSelection = value
            },
            onTimeChange(value) {
                this.log(value)
            },
            onShapeChange(shape) {
                this.log(`On shape change: ${JSON.stringify(shape)}`)

                if ('circle' === shape.type) {
                    this.survey.areaOfPlants = Math.round(Math.PI*Math.pow(shape.radius, 2))
                } else if ('polygon' === shape.type) {
                    let area = computeArea(shape.paths)
                    this.survey.areaOfPlants = Math.round(area * 100) / 100
                } else{
                    this.survey.areaOfPlants = '';
                }

                // Update complex object reference
                this.survey.shape = shape
            },
            onSubmit() {
                if (this.$refs.form.validate()) {
                    // Check existence of shape to locate neophyte
                    if (this.survey.shape === undefined) {
                        // Notify user about circumstance
                        this.$toast(this.$t('key.locatePlantText'), {color: 'red'})
                        return
                    }
                    // Only polygon does not have a position property which is
                    // required for displaying it on the map. Therefore, the
                    // first path item is taken
                    if (this.survey.shape && this.survey.shape.type === Shapes.POLYGON.value
                        && this.survey.shape.paths && this.survey.shape.paths.length > 0) {
                       this.survey.shape.position = this.survey.shape.paths[0]
                    }

                    // Copy image references
                    if (this.imageIds && this.imageIds.length > 0) {
                        // Create & init property
                        this.survey.imageIds = []
                        // Set property
                        for (let imageId of this.imageIds) {
                            this.survey.imageIds.push(imageId)
                        }
                    }

                    // Add reference to contractor
                    if (this.userProfile.role === Roles.ROLE_3.value) {
                        this.survey.contractor = this.userProfile.email
                    }
                    this.$refs.imageeditor.saveState();

                    this.$store.dispatch('applySurvey', this.survey)

                }
            },
            onCancel() {
                this.$router.push({name: 'Surveys'})
            },
            getCurrentPosition() {
                return this.$store.getters.userPosition
            },
            mapMarkerEnabled() {  // Copy of MapEditor
                let plant = this.$store.getters.plantList.find(plant => plant.uuid === this.plantIdSelection)
                return (plant && plant.mapMarker !== undefined) ? plant.mapMarker : false
            },
            mapCircleEnabled() {  // Copy of MapEditor
                let plant = this.$store.getters.plantList.find(plant => plant.uuid === this.plantIdSelection)
                return (plant && plant.mapCircle !== undefined) ? plant.mapCircle : false
            },
            mapPolygonEnabled() { // Copy of MapEditor
                let plant = this.$store.getters.plantList.find(plant => plant.uuid === this.plantIdSelection)
                return (plant && plant.mapPolygon !== undefined) ? plant.mapPolygon : false
            },
            mapActionsEnabled() {
                let actionType = this.actionTypes.find(action => action.value === this.survey.actionType)
                return actionType && actionType.actionsEnabled;
            },
            mapAmountEnabled() {
                let actionType = this.actionTypes.find(action => action.value === this.actionSelection)
                return actionType && actionType.amountEnabled;
            },
            log(text) {
                console.log(`[SurveyForm] ${text}`)
            }
        },
        mounted() {
            this.$store.dispatch('getPlants')
            if(!this.surveyId)
                this.temporaryId = uuid.v4()
            // Initialize data model
/*            if (this.surveyId) {
                // Find given prop in list of surveys
                this.survey = this.$store.getters.surveyList.find(item => item.uuid === this.surveyId)
            } else {
                this.survey = {
                    uuid: uuid.v4(),
                    neophyte: null,
                    vegetationStage: null,
                    numberOfPlants: null,
                    areaOfPlants: null,
                    position: null,
                    action: null,
                    amountValue: null,
                    amountUnit: null,
                    date: moment().format("YYYY-MM-DD"),
                    timeStart: moment().subtract(2, 'h').format("kk:mm"),
                    timeEnd: moment().format("kk:mm"),
                    customer: null,
                    note: null,
                    comment: null
                }
            }
            console.log(this.survey)*/
        },
        created() {
            // Initialize view
            this.currentPosition = this.getCurrentPosition()
        }
    };
</script>

<style lang="scss">
    .container {
        div.v-select {
            padding-left: 0px;
            padding-right: 0px;
        }

        div.blockquote {
            padding-left: 0px;
        }

        div.images-upload {
            padding: 12px 0px;
        }
    }
</style>
